import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import React, { useCallback, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import { ConfigFileStatus } from "../../../entities/ConfigFileStatus";
import { useTerminalContext } from "../contexts/TerminalContext";
import { useConfigFileSaver } from "../hooks/useConfigFileSaver";
import { InfoBox } from "./InfoBox";

interface SavePublishControlsProps {
  terminalId: number | undefined;
  selectedSrc: string | undefined;
}

export const SavePublishControls: React.FC<SavePublishControlsProps> = ({
  terminalId,
  selectedSrc,
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const [saving, setSaving] = useState(false);
  const { setError, configFileStatus } = useTerminalContext();
  const { saveReadyConfig } = useConfigFileSaver();

  const handleSave = useCallback(
    async (status: ConfigFileStatus, errorMessage: string) => {
      try {
        if (!terminalId) {
          setError(translate("osfc.storage_ui.save_publish.no_terminal"));
          return;
        }
        if (!selectedSrc) {
          setError(translate("osfc.storage_ui.save_publish.no_config"));
          return;
        }
        setSaving(true);
        await saveReadyConfig(terminalId, selectedSrc, status);
        setError(null);
        notify(
          status === ConfigFileStatus.production
            ? "osfc.storage_ui.save_publish.published"
            : "osfc.storage_ui.save_publish.draft_saved",
          "success"
        );
      } catch (err: any) {
        setError(err.message || errorMessage);
      } finally {
        setSaving(false);
      }
    },
    [terminalId, selectedSrc, saveReadyConfig, setError, notify, translate]
  );

  const handleSaveDraft = useCallback(
    () => handleSave(ConfigFileStatus.draft, "Failed to save draft"),
    [handleSave]
  );

  const handlePublish = useCallback(
    () => handleSave(ConfigFileStatus.production, "Failed to publish to production"),
    [handleSave]
  );

  return (
    <InfoBox title={translate("osfc.storage_ui.save_publish.title")} style={{ marginBottom: "15px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Tooltip
          title={
            configFileStatus === ConfigFileStatus.production
              ? translate("osfc.storage_ui.save_publish.draft_tooltip")
              : ""
          }
        >
          <span>
            <Button
              onClick={handleSaveDraft}
              variant="contained"
              color="primary"
              disabled={configFileStatus === ConfigFileStatus.production || saving}
              startIcon={saving ? <CircularProgress size={18} /> : undefined}
              fullWidth
            >
              {translate("osfc.storage_ui.save_publish.save_draft")}
            </Button>
          </span>
        </Tooltip>
        <Button
          onClick={handlePublish}
          variant="contained"
          color="secondary"
          fullWidth
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {translate("osfc.storage_ui.save_publish.publish")}
        </Button>
      </div>
    </InfoBox>
  );
};
